/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { DetailsHeader, IColumn, IDetailsHeaderProps } from '@fluentui/react/lib/DetailsList';
import { ILinkProps, ILinkStyleProps, ILinkStyles, Link } from '@fluentui/react/lib/Link';
import { IScrollablePaneProps, IScrollablePaneStyles, ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Stack } from '@fluentui/react/lib/Stack';
import { Sticky } from '@fluentui/react/lib/Sticky';
import { FontWeights, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { IRenderFunction, classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { useSelection } from 'src/core/hooks/useSelection';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { AutoTaggingQuotaConsumptionCard } from 'src/customText/appShell/autoTaggingPage/components/AutoTaggingQuotaConsumptionCard';
import { CustomTextDocumentMetadata } from 'src/customText/core/stores/CustomTextDocumentStore';
import { isDocumentTagged } from 'src/customText/core/utils/documentUtils';
import { AutotagTriggerContentLeftMargin } from 'src/customText/verticals/autotagTriggerModal/autotagTriggerModal';
import { AutoLabelingModalFooter } from 'src/customText/verticals/autotagTriggerModal/components/AutoLabelingModalFooter';
import { ViewFileScreen } from 'src/customText/verticals/autotagTriggerModal/components/ViewFileScreen';
import { AutoLabelingInfoDto, AutoLabelingSteps } from 'src/customText/verticals/autotagTriggerModal/types';
import { contentSizeToTextRecords, getAutoLabelingStepStatus } from 'src/customText/verticals/autotagTriggerModal/utils';

export type AutotagTriggerModalSelectFilesProps = TableProps<CustomTextDocumentMetadata> & {
    autotagInfoDto: AutoLabelingInfoDto;
    onSubmit: (documents: CustomTextDocumentMetadata[], selectedChars: number) => void;
    onDismiss: () => void;
    onPrevious: () => void;
};

const messages = defineMessages({
    filter: { id: 'customText.selectFilesScreen.filter', defaultMessage: 'Filter' },
    search: { id: 'customText.selectFilesScreen.search', defaultMessage: 'Search' },
    language: { id: 'customText.selectFilesScreen.language', defaultMessage: 'Language' },
    loading: { id: 'customText.selectFilesScreen.loading', defaultMessage: 'Loading...' },
    documents: { id: 'customText.selectFilesScreen.documents', defaultMessage: 'Documents' },
    state: { id: 'customText.selectFilesScreen.state', defaultMessage: 'Labeled/Unlabeled' },
    textRecords: { id: 'customText.selectFilesScreen.textRecords', defaultMessage: 'Text records' },
    count: { id: 'customText.selectFilesScreen.count', defaultMessage: '{count} documents selected' },
    allDocuments: { id: 'customText.selectFilesScreen.allDocuments', defaultMessage: 'All documents' },
    title: { id: 'customText.selectFilesScreen.title', defaultMessage: 'Select documents to be auto-labeled' },
    labeledDocuments: { id: 'customText.selectFilesScreen.labeledDocuments', defaultMessage: 'Labeled documents' },
    loadMoreDocuments: { id: 'customText.selectFilesScreen.loadMoreDocuments', defaultMessage: 'Load more documents' },
    unlabeledDocuments: { id: 'customText.selectFilesScreen.unlabeledDocuments', defaultMessage: 'Unlabeled documents' },
    description: {
        id: 'customText.selectFilesScreen.description',
        defaultMessage:
            'Select the documents that you want to auto-label. You can select all documents and you can view any document you want.'
    }
});

const { styleComponent } = getStylistV2('SelectFilesScreen');

const ScrollablePaneStyles = classNamesFunction<IScrollablePaneProps, IScrollablePaneStyles>()({
    root: { height: '100%', position: 'relative' }
});

const commandBarStyles = { padding: '20px 20px 10px 32px' };

const LoadMoreLink = styleComponent<ILinkProps>(Link)('LoadMoreLink', (_, cssVars) => ({
    background: cssVars.background,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 30,
    paddingRight: 8,
    boxSizing: 'border-box',
    cursor: 'pointer'
}));

const nameLinkStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({
    root: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        maxWidth: 'calc(100% - 24px)'
    }
});

type filesSortKey = { key: string | ((doc: CustomTextDocumentMetadata) => boolean); order: 'asc' | 'desc' };

export const SelectFilesScreen = observer((props: AutotagTriggerModalSelectFilesProps) => {
    const { autotagInfoDto, onSubmit, onDismiss, onPrevious, ...rest } = props;
    const { localize } = useLocalization();
    const loadNextBatchTracker = useProgressTracker();
    const { isXLargeOrGreater } = useResponsiveMode();
    const { rootStore, dispatcher } = useGlobalState();
    const { selection, selectedItems } = useSelection<CustomTextDocumentMetadata>();

    const documents = rootStore.currentCustomTextProjectStore.documents ?? [];

    const [searchQuery, setSearchQuery] = React.useState('');
    const [selectedFile, setSelectedFile] = React.useState(null);
    const [shownDocuments, setShownDocuments] = React.useState(documents);
    const [selectedTextRecords, setSelectedTextRecords] = React.useState(0);
    const [shownDocumentsState, setShownDocumentsState] = React.useState('all');
    const [sortKey, setSortKey] = React.useState<filesSortKey>({ key: isDocumentTagged, order: 'asc' });

    const isGptJob = autotagInfoDto.autoLabelingJobType === 'gpt';
    const autoTaggingQuota = rootStore.currentCustomTextProjectStore.autoTaggingQuota || {
        consumedQuota: 0,
        totalQuota: selectedTextRecords
    };

    React.useEffect(() => {
        autotagInfoDto.documents.forEach(doc => selection.setKeySelected(doc.name, true, false));
    }, [autotagInfoDto]);

    React.useEffect(() => {
        switch (shownDocumentsState) {
            case 'all':
                setShownDocuments(documents);
                break;
            case 'tagged':
                setShownDocuments(documents.filter(doc => isDocumentTagged(doc)));
                break;
            case 'untagged':
                setShownDocuments(documents.filter(doc => !isDocumentTagged(doc)));
                break;
            default:
        }
    }, [documents]);

    const handleLoadNextBatchOfDocuments = (tracker?: ProgressTrackerStore) => {
        dispatcher.dispatch('getCustomTextNextBatchOfDocuments', { tracker });
    };

    const handleFilteringDocuments = (state: string) => {
        switch (state) {
            case 'all':
                setShownDocuments(documents);
                setShownDocumentsState('all');
                break;
            case 'tagged':
                setShownDocuments(documents.filter(doc => isDocumentTagged(doc)));
                setShownDocumentsState('tagged');
                break;
            case 'untagged':
                setShownDocuments(documents.filter(doc => !isDocumentTagged(doc)));
                setShownDocumentsState('untagged');
                break;
            default:
        }
    };

    React.useEffect(() => {
        if (searchQuery.length > 0) {
            dispatcher.dispatch('getCustomTextSpecificBatchWithPrefix', { prefix: searchQuery });
        }
    }, [searchQuery]);

    const filteredDocuments = React.useMemo(
        () => orderBy(substringSearchStrategy(shownDocuments, searchQuery, ['name']), sortKey.key, [sortKey.order]),
        [searchQuery, shownDocuments, sortKey]
    );

    React.useEffect(() => {
        const total = selectedItems.reduce((val, acc) => contentSizeToTextRecords(acc.contentSize, acc.language) + val, 0);
        setSelectedTextRecords(total);
    }, [selectedItems]);

    const handleColumnClick = (c: IColumn) => {
        if (c.key === 'state') {
            setSortKey({ key: isDocumentTagged, order: c.isSortedDescending ? 'asc' : 'desc' });
        } else {
            setSortKey({ key: c.key, order: c.isSortedDescending ? 'asc' : 'desc' });
        }
    };

    const handleFilterClick = (event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement, MouseEvent>, filter: string) => {
        event.preventDefault();
        handleFilteringDocuments(filter);
    };

    const columns: IColumn[] = [
        {
            key: 'name',
            name: localize(messages.documents),
            fieldName: 'files',
            minWidth: 50,
            maxWidth: 150,
            isRowHeader: true,
            isResizable: true,
            isSorted: sortKey?.key === 'name',
            isSortedDescending: sortKey?.key === 'name' && sortKey?.order === 'desc',
            onRender: (item: CustomTextDocumentMetadata) => (
                <NameCellLink data-automation-id="documentCell" styles={nameLinkStyles} onClick={() => setSelectedFile(item)}>
                    {item.name}
                </NameCellLink>
            )
        },
        ...(!isGptJob && rootStore.currentCustomTextProjectStore.multiLingual
            ? [
                  {
                      key: 'language',
                      name: localize(messages.language),
                      fieldName: 'language',
                      minWidth: 150,
                      maxWidth: 300,
                      isRowHeader: true,
                      isResizable: true,
                      isSorted: sortKey?.key === 'language',
                      isSortedDescending: sortKey?.key === 'language' && sortKey?.order === 'desc',
                      onRender: (item: CustomTextDocumentMetadata) => (
                          <LuisDropdown
                              key={item.language}
                              disabled={rootStore.customTextCultureStorehouse.tracker.isRunning}
                              data-selection-disabled
                              onChange={(_, option) => {
                                  documents.find(doc => doc.name === item.name).language = option.key as string;
                              }}
                              options={rootStore.customTextCultureStorehouse.items.map(culture => ({
                                  key: culture.code,
                                  text: culture.name,
                                  selected: culture.code === item.language
                              }))}
                          />
                      )
                  }
              ]
            : []),
        {
            key: 'state',
            name: localize(messages.state),
            fieldName: 'state',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            isSorted: sortKey?.key === isDocumentTagged,
            isSortedDescending: sortKey?.key === isDocumentTagged && sortKey?.order === 'desc',
            onRender: (item: CustomTextDocumentMetadata) => <LuisText>{isDocumentTagged(item) ? 'Labeled' : 'Unlabeled'}</LuisText>
        },
        ...(!isGptJob
            ? [
                  {
                      key: 'contentSize',
                      name: localize(messages.textRecords),
                      fieldName: 'textRecords',
                      minWidth: 100,
                      maxWidth: 150,
                      isResizable: true,
                      isSorted: sortKey?.key === 'contentSize',
                      isSortedDescending: sortKey?.key === 'contentSize' && sortKey?.order === 'desc',
                      onRender: (item: CustomTextDocumentMetadata) => (
                          <LuisText>{contentSizeToTextRecords(item.contentSize, item.language)}</LuisText>
                      )
                  }
              ]
            : [])
    ];

    const filterMenuItems: IContextualMenuItem[] = [
        {
            key: 'allFiles',
            canCheck: true,
            text: localize(messages.allDocuments),
            checked: shownDocumentsState === 'all',
            onClick: _event => handleFilterClick(_event, 'all')
        },
        {
            key: 'tagged',
            canCheck: true,
            text: localize(messages.labeledDocuments),
            checked: shownDocumentsState === 'tagged',
            onClick: _event => handleFilterClick(_event, 'tagged')
        },
        {
            canCheck: true,
            key: 'untaggedFiles',
            text: localize(messages.unlabeledDocuments),
            checked: shownDocumentsState === 'untagged',
            onClick: _event => handleFilterClick(_event, 'untagged')
        }
    ];

    const farItems = [
        {
            key: 'selectedItemsCount',
            onRender: () => (
                <LuisText styles={{ root: { display: 'flex', alignItems: 'center', marginRight: '5px' } }}>
                    {localize(messages.count, { count: selectedItems.length })}
                </LuisText>
            )
        },
        {
            key: 'filter',
            text: localize(messages.filter),
            iconProps: { iconName: 'Filter' },
            subMenuProps: { items: filterMenuItems }
        },
        {
            key: 'search',
            onRender: () => (
                <LuisDebouncedSearchBox
                    ariaLabel={localize(messages.search)}
                    styles={{ root: { marginTop: '5px' } }}
                    size={isXLargeOrGreater ? 24 : 20}
                    value={searchQuery}
                    placeholder={localize(messages.search)}
                    onChange={(_, newValue?: string) => setSearchQuery(newValue)}
                />
            )
        }
    ];

    const onRenderHeader: IRenderFunction<IDetailsHeaderProps> = headerProps => (
        <Sticky>
            <DetailsHeader {...headerProps} />
        </Sticky>
    );

    return selectedFile ? (
        <ViewFileScreen document={selectedFile} onDismiss={() => setSelectedFile(null)} />
    ) : (
        <Stack verticalFill grow>
            <Stack
                styles={{ root: { height: '100px', overflowY: 'auto' } }}
                tokens={{ childrenGap: 24, padding: `0 0 0 ${AutotagTriggerContentLeftMargin}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    <LuisText>{localize(messages.description)}</LuisText>
                </Stack>
            </Stack>
            {!isGptJob && (
                <Stack.Item tokens={{ padding: `0 0 0 ${AutotagTriggerContentLeftMargin}` }}>
                    <AutoTaggingQuotaConsumptionCard
                        totalConsumedQuota={autoTaggingQuota.consumedQuota}
                        totalMonthlyQuota={autoTaggingQuota.totalQuota}
                        selectedTextRecords={selectedTextRecords}
                    />
                </Stack.Item>
            )}
            <Stack.Item tokens={commandBarStyles}>
                <LuisCommandBar items={[]} farItems={farItems} showTopBorder={false} />
            </Stack.Item>
            <Stack.Item grow tokens={{ padding: `0 0 0 ${AutotagTriggerContentLeftMargin}` }}>
                <ScrollablePane
                    styles={mergeStyleSets(ScrollablePaneStyles, { root: { minHeight: isXLargeOrGreater ? '230px' : '150px' } })}>
                    <Table<CustomTextDocumentMetadata>
                        items={filteredDocuments.map(doc => ({ key: doc.name, ...doc }))}
                        columns={columns}
                        selection={selection}
                        selectionPreservedOnEmptyClick
                        onSortColumn={handleColumnClick}
                        onRenderDetailsHeader={onRenderHeader}
                        selectionPredicate={item => !!selectedItems.find(x => x.name === item.key)}
                        {...rest}
                    />
                    {rootStore.currentCustomTextProjectStore.nextBatchExists && shownDocumentsState !== 'tagged' && (
                        <LoadMoreLink
                            style={{ display: 'flex', flex: 1 }}
                            onClick={() => handleLoadNextBatchOfDocuments(loadNextBatchTracker)}
                            disabled={loadNextBatchTracker.isRunning}>
                            {loadNextBatchTracker.isRunning ? localize(messages.loading) : localize(messages.loadMoreDocuments)}
                        </LoadMoreLink>
                    )}
                </ScrollablePane>
            </Stack.Item>
            <AutoLabelingModalFooter
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                isLoading={loadNextBatchTracker.isRunning}
                activeStep={AutoLabelingSteps.SelectFiles}
                isFooterDisabled={loadNextBatchTracker.isRunning}
                onNext={() => onSubmit(selectedItems, selectedTextRecords)}
                isValid={
                    getAutoLabelingStepStatus(
                        AutoLabelingSteps.SelectFiles,
                        AutoLabelingSteps.SelectFiles,
                        { ...autotagInfoDto, documents: selectedItems },
                        rootStore
                    ).isValid
                }
            />
        </Stack>
    );
});
